import React, { FC, useEffect, useMemo, useState } from 'react';

import { EvSection } from '@evinced-private/ui-common';

import {
	WFA_SESSION_COMPONENTS_TAB_DAID,
	WFA_SESSION_COMPONENTS_TABLE_DAID
} from 'data-automation-ids/WfaDashboardAutomationIds';
import { WfaSessionComponentsSkeleton } from 'src/apps/wfa/components/skeletons';
import { useWfaSessionComponentsQuery } from 'src/apps/wfa/hooks/useWfaSessionComponentsQuery';
import { useWfaSessionDetailsQuery } from 'src/apps/wfa/hooks/useWfaSessionDetailsQuery';
import { PlatformTable } from 'src/common/components/platform-table/PlatformTable';
import { TableSkeleton } from 'src/common/components/skeletons/table-skeleton/TableSkeleton';
import { buildFullTableId } from 'src/common/helpers/TableStateHelper';
import {
	WFA_SESSION_COMPONENTS_TABLE_ID,
	WFA_SESSION_ISSUES_TABLE_ID
} from 'src/common/pages/dashboards-container/dashboard_consts';
import { useConfiguration } from 'src/common/providers/configurationProvider/ConfigurationProvider';
import { useTableState } from 'src/common/providers/dashboard/TableStateProvider';

import { WfaSingleSessionFiltersSection } from '../../filters-section/WfaSingleSessionFiltersSection';

import { getWfaComponentsColumns } from './WfaSessionComponentsTableHelper';

import './WfaSessionComponents.scss';

interface IWfaSessionComponentsProps {
	sessionId: string;
}

export const WfaSessionComponents: FC<IWfaSessionComponentsProps> = ({ sessionId }) => {
	const { getTableStateById, onFilterChange } = useTableState();
	const { configuration } = useConfiguration();
	const isLabelsToggleEnabled = (configuration?.WFA?.TOGGLES as Record<string, boolean>)
		?.ENABLE_PLATFORM_LABELS as boolean;

	const tableId = useMemo(
		() => buildFullTableId(WFA_SESSION_COMPONENTS_TABLE_ID, sessionId),
		[sessionId]
	);
	const tableState = getTableStateById(tableId);
	const { filters, tableSort, paginationInfo } = tableState;

	const sessionDetailsQuery = useWfaSessionDetailsQuery(sessionId, isLabelsToggleEnabled);

	const componentsQuery = useWfaSessionComponentsQuery({
		sessionId,
		sort: tableSort?.[0],
		page: paginationInfo.page,
		pageSize: paginationInfo.sizePerPage,
		filters
	});

	const handleComponentClicked = (componentId: string): void => {
		const issuesTableIdForSession = buildFullTableId(WFA_SESSION_ISSUES_TABLE_ID, sessionId);
		onFilterChange(issuesTableIdForSession, {
			searchValue: componentId
		});
	};

	const { total = 0, unfilteredTotal = 0, rows = [] } = componentsQuery.data ?? {};

	const isTableLoading = componentsQuery.isLoading || componentsQuery.isPreviousData;
	const isPageLoading =
		sessionDetailsQuery.isLoading || (!sessionDetailsQuery.data && isTableLoading);

	const [showInitialSkeleton, setShowInitialSkeleton] = useState(isPageLoading);

	useEffect(() => {
		if (!isPageLoading && showInitialSkeleton) {
			setShowInitialSkeleton(false);
		}
	}, [isPageLoading, showInitialSkeleton]);

	if (showInitialSkeleton) {
		return <WfaSessionComponentsSkeleton isOverlay />;
	}

	return (
		<EvSection
			className="wfa-session-components"
			dataAutomationId={WFA_SESSION_COMPONENTS_TAB_DAID}
		>
			<WfaSingleSessionFiltersSection
				sessionId={sessionId}
				filters={filters}
				ariaLabel="WFA Session's components filters"
				onFilterChange={onFilterChange}
				tableId={tableId}
			/>
			<EvSection ariaLabel="WFA Session's components table" skipLinkId="main-section">
				{isTableLoading ? (
					<TableSkeleton columns={4} rowCount={5} />
				) : (
					<PlatformTable
						dataAutomationId={WFA_SESSION_COMPONENTS_TABLE_DAID}
						isTableLoading={componentsQuery.isLoading || componentsQuery.isPreviousData}
						tableId={tableId}
						tableData={rows}
						totalCount={unfilteredTotal}
						totalPaginationableResults={total}
						className="session-components-table"
						persistTableState
						columns={getWfaComponentsColumns(sessionId, handleComponentClicked, tableId)}
						options={{
							keyField: 'id',
							title: 'WFA Session Components',
							caption: 'WFA Session Components table',
							pagination: true,
							dataType: 'components',
							remote: true
						}}
						withUrlSwitcher={true}
					/>
				)}
			</EvSection>
		</EvSection>
	);
};
